// src/features/records/components/FilterModal.js
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Search } from 'lucide-react';
import useFilterStore from '../stores/useFilterStore';
import { useUser } from '../../../hooks';
import { useGroups } from '../../groups';
import useFriends from '../../../hooks/useFriends';
import useUserProfiles from '../hooks/useUserProfiles';
import useSharedRecordCounts from '../hooks/useSharedRecordCounts';
import useGroupRecordCounts from '../hooks/useGroupRecordCounts';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';
import useFocusTrap from '../../../hooks/useFocusTrap';
import { useDebounce } from '../../../hooks/useDebounce';
import useBottomSheetDrag from '../../../hooks/useBottomSheetDrag';
import { useIsMobileLike } from '../../../hooks/useIsMobileLike';
import { Avatar, AVATAR_VARIANTS } from '../../../components/avatars';
import styles from './FilterModal.module.css';

/**
 * Filter modal component with Users/Groups tabs
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close callback
 */
function FilterModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const { user } = useUser();
  const scope = useFilterStore((state) => state.scope);
  const setScope = useFilterStore((state) => state.setScope);
  const {
    groups,
    loading: groupsLoading,
    loadError: groupsError,
    status: groupsStatus,
  } = useGroups();
  const { friends, loading: friendsLoading } = useFriends();
  const modalRef = React.useRef(null);
  const overlayRef = React.useRef(null);
  const selectionRef = React.useRef(null); // For animation
  
  // Use focus trap for accessibility
  useFocusTrap(modalRef, isOpen);
  
  // Lock body scroll when modal is open
  useBodyScrollLock(isOpen);
  
  // Detect mobile for drag-to-close functionality
  const isMobileLike = useIsMobileLike();
  
  // Use drag-to-close on mobile
  const { bindHandle } = useBottomSheetDrag({
    enabled: isMobileLike && isOpen,
    sheetRef: modalRef,
    overlayRef,
    onClose,
    options: { 
      startThresholdPx: 8,  // Only start drag after 8px movement (prevent accidental drags)
      fadeBackdrop: false   // Don't fade backdrop to avoid fading modal content
    }
  });
  
  const selfId = user?.uid || null;
  
  // Ensure friends array is stable across renders
  const friendsSafe = useMemo(
    () => Array.isArray(friends) ? friends : [],
    [friends]
  );
  
  // Create a Map for O(1) friend lookups
  const friendById = useMemo(
    () => new Map(friendsSafe.map(f => [f.id, f])),
    [friendsSafe]
  );
  
  // Local state for preview (not committed until Apply)
  const [activeTab, setActiveTab] = useState(scope.mode === 'group' ? 'groups' : 'users');
  const [selectedUserIds, setSelectedUserIds] = useState(
    scope.mode === 'users' ? (scope.userIds || []) : (selfId ? [selfId] : [])
  );
  const [selectedGroupId, setSelectedGroupId] = useState(
    scope.mode === 'group' ? scope.groupId : null
  );
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [groupSearchQuery, setGroupSearchQuery] = useState('');

  const selfLabel = t('records.filters.self_label');
  const fallbackGroupsLoadErrorMessage = t('groups.load_error');
  const groupsErrorMessage = groupsError ? t(groupsError, fallbackGroupsLoadErrorMessage) : fallbackGroupsLoadErrorMessage;
  const groupsOfflineMessage = t('groups.offline_error');
  const isGroupsOfflineState = groupsStatus === 'offline';
  
  // Debounce search queries for performance (300ms delay)
  const debouncedUserSearchQuery = useDebounce(userSearchQuery, 300);
  const debouncedGroupSearchQuery = useDebounce(groupSearchQuery, 300);
  
  // Get user profiles for display - filter out undefined/null IDs
  const allUserIds = useMemo(() => {
    const ids = new Set();
    if (selfId) ids.add(selfId);
    friendsSafe.forEach(friend => {
      if (friend?.id) ids.add(friend.id);
    });
    return Array.from(ids);
  }, [selfId, friendsSafe]);
  
  const { profiles: userProfiles } = useUserProfiles(allUserIds);
  
  // Get shared record counts for all friends - filter out undefined IDs
  const friendIds = useMemo(() => friendsSafe.map(f => f.id).filter(Boolean), [friendsSafe]);
  const { counts: sharedRecordCounts, loading: countsLoading } = useSharedRecordCounts(
    friendIds,
    { enabled: isOpen } // Only fetch when modal is open
  );
  
  // Get record counts for all groups - filter out undefined IDs
  const groupIds = useMemo(() => groups.map(g => g.id).filter(Boolean), [groups]);
  const { counts: groupRecordCounts, loading: groupCountsLoading } = useGroupRecordCounts(
    groupIds,
    { enabled: isOpen } // Only fetch when modal is open
  );
  
  // Helper function to trigger selection content animation
  const triggerSelectionAnimation = () => {
    // Use requestAnimationFrame for better performance and reliability
    requestAnimationFrame(() => {
      const content = selectionRef.current;
      if (content) {
        content.classList.remove(styles.pulseOnce);
        void content.offsetWidth; // Force reflow to restart animation
        content.classList.add(styles.pulseOnce);
      }
    });
  };
  
  // Prepare self user
  const selfUser = useMemo(() => ({
    id: selfId,
    name: userProfiles[selfId]?.displayName || selfLabel,
    avatarUrl: userProfiles[selfId]?.profilePictureUrl,
    isSelf: true
  }), [selfId, selfLabel, userProfiles]);
  
  // Filter friends based on search (uses debounced query for performance)
  const filteredFriends = useMemo(() => {
    if (!debouncedUserSearchQuery.trim()) return friendsSafe;
    
    const query = debouncedUserSearchQuery.toLowerCase();
    return friendsSafe.filter(friend => {
      const profile = userProfiles[friend.id];
      const name = profile?.displayName || friend.name || '';
      return name.toLowerCase().includes(query);
    });
  }, [friendsSafe, debouncedUserSearchQuery, userProfiles]);
  
  // Filter groups based on search (uses debounced query for performance)
  const filteredGroups = useMemo(() => {
    if (!debouncedGroupSearchQuery.trim()) return groups;
    
    const query = debouncedGroupSearchQuery.toLowerCase();
    return groups.filter(group => {
      return group.name?.toLowerCase().includes(query);
    });
  }, [groups, debouncedGroupSearchQuery]);
  
  const selectedGroup = useMemo(() => {
    if (!selectedGroupId) return null;
    return groups.find(group => group.id === selectedGroupId) || null;
  }, [selectedGroupId, groups]);
  
  // Handle tab switch
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    // Don't clear selections when switching tabs
    // This preserves the user's work
  };
  
  // Toggle user selection
  const toggleUser = (userId) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      }
      return [...prev, userId];
    });
    triggerSelectionAnimation();
  };
  
  // Select group (single selection)
  const selectGroup = (groupId) => {
    setSelectedGroupId(groupId === selectedGroupId ? null : groupId);
    triggerSelectionAnimation();
  };
  
  // Compute action label
  const getActionLabel = () => {
    if (activeTab === 'groups' && selectedGroupId) {
      return t('records.filters.actions.apply_group');
    }
    
    if (activeTab === 'users') {
      const count = selectedUserIds.length;
      if (count === 0 || (count === 1 && selectedUserIds[0] === selfId)) {
        return t('records.filters.actions.apply_self');
      }
      if (count === 1) {
        return t('records.filters.actions.apply_user');
      }
      return t('records.filters.actions.apply_blend_count', { count });
    }
    
    return t('records.filters.actions.apply_blend');
  };
  
  // Clear all selections
  const handleClearAll = () => {
    setSelectedUserIds([]);
    setSelectedGroupId(null);
    // Reset search queries as well
    setUserSearchQuery('');
    setGroupSearchQuery('');
    triggerSelectionAnimation();
  };
  
  // Handle apply
  const handleApply = () => {
    if (activeTab === 'groups' && selectedGroupId) {
      setScope({ mode: 'group', groupId: selectedGroupId });
    } else {
      // Default to users mode with at least self selected
      const userIds = selectedUserIds.length > 0 ? selectedUserIds : [selfId];
      setScope({ mode: 'users', userIds });
    }
    onClose();
  };
  
  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  // Get the modal root element for portal rendering
  const modalRoot = typeof document !== 'undefined' 
    ? document.getElementById('modal-root') || document.body
    : null;
  
  const modalContent = (
    <div ref={overlayRef} className={styles.overlay} onClick={onClose}>
      <div 
        ref={modalRef}
        className={styles.modal} 
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="filter-modal-title"
      >
        {/* Drag handle – visible only on mobile via CSS */}
        <div
          className={styles.dragHandle}
          role="button"
          aria-label={t('records.filters.modal.drag_to_close_aria')}
          {...bindHandle}
        />
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerRow}>
            <h2 id="filter-modal-title" className={styles.title}>{t('records.filters.modal.title')}</h2>
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label={t('records.filters.modal.close_aria')}
            >
              <X size={16} />
            </button>
          </div>
          
          {/* Tab Switcher */}
          <div className={styles.tabSwitcher} role="tablist">
            <button
              className={`${styles.tab} ${activeTab === 'users' ? styles.active : ''}`}
              onClick={() => handleTabSwitch('users')}
              role="tab"
              aria-selected={activeTab === 'users'}
              aria-controls="users-panel"
            >
              {t('records.filters.tabs.users')}
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'groups' ? styles.active : ''}`}
              onClick={() => handleTabSwitch('groups')}
              role="tab"
              aria-selected={activeTab === 'groups'}
              aria-controls="groups-panel"
            >
              {t('records.filters.tabs.groups')}
            </button>
            <div className={`${styles.tabIndicator} ${activeTab === 'groups' ? styles.right : ''}`} />
          </div>
        </div>
        
        {/* Users Tab */}
        {activeTab === 'users' && (
          <div id="users-panel" role="tabpanel" className={styles.tabPanel}>
            {/* Search */}
            <div className={styles.searchContainer}>
              <Search size={16} className={styles.searchIcon} />
              <input
                type="search"
                placeholder={t('records.filters.users.search_placeholder')}
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className={styles.searchInput}
                aria-label={t('records.filters.users.search_aria')}
              />
            </div>
            
            {/* Users List */}
            <div className={styles.listContainer} role="group" aria-label={t('records.filters.users.list_aria')}>
              {/* Self (pinned) */}
              <div className={styles.sectionLabel}>{t('records.filters.users.section_me')}</div>
              <UserItem
                user={selfUser}
                isSelected={selectedUserIds.includes(selfId)}
                onToggle={() => toggleUser(selfId)}
                isPinned
                sharedRecords={0}
              />
              
              {/* Friends */}
              {friendsSafe.length > 0 && (
                <div className={styles.sectionLabel}>{t('records.filters.users.section_friends')}</div>
              )}
              {friendsLoading ? (
                <div className={styles.loading}>{t('records.filters.users.loading')}</div>
              ) : (
                filteredFriends.map((friend) => {
                  const profile = userProfiles[friend.id];
                  const friendUser = {
                    id: friend.id,
                    name: profile?.displayName || friend.name || t('records.filters.users.friend_fallback_name'),
                    avatarUrl: friend.photoURL || profile?.profilePictureUrl,
                    isSelf: false
                  };
                  
                  // Get actual shared records count from the hook
                  const sharedRecords = sharedRecordCounts.get(friend.id) || 0;
                  
                  return (
                    <UserItem
                      key={friend.id}
                      user={friendUser}
                      isSelected={selectedUserIds.includes(friend.id)}
                      onToggle={() => toggleUser(friend.id)}
                      sharedRecords={sharedRecords}
                      loading={countsLoading}
                    />
                  );
                })
              )}
              
              {!friendsLoading && filteredFriends.length === 0 && userSearchQuery && (
                <div className={styles.empty}>{t('records.filters.users.empty_search')}</div>
              )}
            </div>
          </div>
        )}
        
        {/* Groups Tab */}
        {activeTab === 'groups' && (
          <div id="groups-panel" role="tabpanel" className={styles.tabPanel}>
            {/* Search */}
            <div className={styles.searchContainer}>
              <Search size={16} className={styles.searchIcon} />
              <input
                type="search"
                placeholder={t('records.filters.groups.search_placeholder')}
                value={groupSearchQuery}
                onChange={(e) => setGroupSearchQuery(e.target.value)}
                className={styles.searchInput}
                aria-label={t('records.filters.groups.search_aria')}
              />
            </div>
            
            {/* Groups List */}
            <div className={styles.listContainer} role="radiogroup" aria-label={t('records.filters.groups.list_aria')}>
              {groups.length > 0 && (
                <div className={styles.sectionLabel}>{t('records.filters.groups.section_your_groups')}</div>
              )}
              {groupsLoading ? (
                <div className={styles.loading}>{t('records.filters.groups.loading')}</div>
              ) : groupsError && groups.length === 0 ? (
                <div className={styles.empty} role="alert">
                  {groupsErrorMessage}
                </div>
              ) : isGroupsOfflineState && groups.length === 0 ? (
                <div className={styles.empty} role="alert">
                  {groupsOfflineMessage}
                </div>
              ) : (
                filteredGroups.map((group) => {
                  // Get actual record count from the hook
                  const recordCount = groupRecordCounts.has(group.id)
                    ? groupRecordCounts.get(group.id)
                    : null;
                  const enhancedGroup = {
                    ...group,
                    recordCount
                  };
                  
                  return (
                    <GroupItem
                      key={group.id}
                      group={enhancedGroup}
                      isSelected={selectedGroupId === group.id}
                      onSelect={() => selectGroup(group.id)}
                      loading={groupCountsLoading}
                    />
                  );
                })
              )}
              
              {!groupsLoading && !groupsError && !isGroupsOfflineState && filteredGroups.length === 0 && groupSearchQuery && (
                <div className={styles.empty}>{t('records.filters.groups.empty_search')}</div>
              )}
              
              {!groupsLoading && !groupsError && !isGroupsOfflineState && groups.length === 0 && !groupSearchQuery && (
                <div className={styles.empty}>{t('records.filters.groups.empty_none')}</div>
              )}
            </div>
          </div>
        )}
        
        {/* Selection Summary */}
        {(selectedUserIds.length > 0 || selectedGroupId) && (
          <div className={`${styles.selectionSummary} ${styles.hasSelection}`}>
            <div className={styles.selectionTitle}>{t('records.filters.selection.title')}</div>
            <div className={styles.selectionContent} ref={selectionRef}>
              <div className={styles.selectionAvatars}>
                {activeTab === 'groups' && selectedGroupId ? (
                  <Avatar
                    variant={AVATAR_VARIANTS.GROUP}
                    name={selectedGroup?.name || t('records.filters.group_fallback_name')}
                    src={selectedGroup?.icon}
                    size="sm"
                    className={styles.selectionAvatar}
                    alt={t('records.filters.group_avatar_alt', {
                      name: selectedGroup?.name || t('records.filters.group_fallback_name')
                    })}
                  />
                ) : (
                  selectedUserIds.slice(0, 5).map((id, index) => {
                    const friend = friendById.get(id);
                    const profile = userProfiles[id];
                    const user = id === selfId ? selfUser : {
                      id,
                      name: profile?.displayName || friend?.name || t('records.filters.user_fallback_name'),
                      avatarUrl: friend?.photoURL || profile?.profilePictureUrl,
                      isSelf: false
                    };
                    const avatarUrl = id === selfId ? selfUser.avatarUrl : user.avatarUrl;
                    const displayName = user.name;
                    return (
                      <Avatar
                        key={id}
                        name={displayName}
                        src={avatarUrl}
                        size="sm"
                        className={styles.selectionAvatar}
                        style={{ zIndex: 5 - index }}
                        alt={displayName}
                      />
                    );
                  })
                )}
                {selectedUserIds.length > 5 && (
                  <div className={`${styles.selectionAvatar} ${styles.selectionAvatarCount}`}>
                    {t('records.filters.selection.avatar_more_count', { count: selectedUserIds.length - 5 })}
                  </div>
                )}
              </div>
              <div className={styles.selectionDescription}>
                {activeTab === 'groups' && selectedGroupId
                  ? selectedGroup?.name || t('records.filters.group_fallback_name')
                  : selectedUserIds.length === 1 && selectedUserIds[0] === selfId
                  ? t('records.filters.selection.only_your_records')
                  : selectedUserIds.length === 1
                  ? userProfiles[selectedUserIds[0]]?.displayName || t('records.filters.user_fallback_name')
                  : (() => {
                      // Create user-friendly name display
                      const names = selectedUserIds.map(id => {
                        if (id === selfId) return t('records.filters.selection.you_label');
                        const profile = userProfiles[id];
                        const friendData = friendById.get(id);
                        return profile?.displayName || friendData?.name || t('records.filters.user_fallback_name');
                      });
                      
                      if (names.length <= 3) {
                        // Show all names if 3 or fewer
                        return names.join(', ');
                      } else {
                        // Show first 2 names and count of others
                        return t('records.filters.selection.names_overflow', {
                          names: names.slice(0, 2).join(', '),
                          count: names.length - 2
                        });
                      }
                    })()
                }
              </div>
            </div>
          </div>
        )}
        
        {/* Footer */}
        <div className={styles.footer}>
          <button
            className={styles.clearButton}
            onClick={handleClearAll}
          >
            {t('records.filters.actions.clear_all')}
          </button>
          <button
            className={styles.applyButton}
            onClick={handleApply}
          >
            {getActionLabel()}
          </button>
        </div>
      </div>
    </div>
  );
  
  // Render the modal in a portal to escape stacking contexts
  return modalRoot ? createPortal(modalContent, modalRoot) : modalContent;
}

/**
 * User item component
 */
function UserItem({ user, isSelected, onToggle, isPinned, sharedRecords, loading }) {
  const { t } = useTranslation();

  return (
    <div 
      className={`${styles.item} ${isSelected ? styles.selected : ''} ${isPinned ? styles.pinned : ''}`}
      onClick={onToggle}
      role="checkbox"
      aria-checked={isSelected}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <Avatar
        name={user.name}
        src={user.avatarUrl}
        className={styles.listAvatar}
        alt={t('records.filters.user_avatar_alt', { name: user.name })}
      />
      <div className={styles.listInfo}>
        <div className={styles.listName}>
          {user.name}
          {user.isSelf && <span className={styles.itemTag}>({t('records.filters.you_suffix')})</span>}
        </div>
        {sharedRecords !== undefined && !user.isSelf && (
          <div className={styles.listMeta}>
            {loading ? (
              <span style={{ opacity: 0.6 }}>{t('records.filters.loading_inline')}</span>
            ) : (
              t('records.filters.shared_records', { count: sharedRecords })
            )}
          </div>
        )}
        {user.isSelf && (
          <div className={styles.listMeta}>{t('records.filters.self_records_description')}</div>
        )}
      </div>
      <div className={`${styles.selectionIndicator} ${styles.userIndicator}`} />
    </div>
  );
}

/**
 * Group item component
 */
function GroupItem({ group, isSelected, onSelect, loading }) {
  const { t } = useTranslation();
  const memberCount = typeof group.memberCount === 'number' ? group.memberCount : null;
  const memberCountLabel = memberCount === null
    ? t('records.filters.group_member_count_unknown')
    : t('records.filters.group_member_count', { count: memberCount });

  const recordCount = typeof group.recordCount === 'number' ? group.recordCount : null;
  let recordCountLabel;
  if (recordCount === null && loading) {
    recordCountLabel = <span style={{ opacity: 0.6 }}>{t('records.filters.loading_inline')}</span>;
  } else if (recordCount === null) {
    recordCountLabel = t('records.filters.group_record_count_unknown');
  } else {
    recordCountLabel = t('records.filters.group_record_count', { count: recordCount });
  }

  return (
    <div 
      className={`${styles.item} ${isSelected ? styles.selected : ''}`}
      onClick={onSelect}
      role="radio"
      aria-checked={isSelected}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <Avatar
        variant={AVATAR_VARIANTS.GROUP}
        name={group.name}
        src={group.icon}
        className={styles.listAvatar}
        shape="rounded"
        alt={t('records.filters.group_avatar_alt', { name: group.name })}
      />
      <div className={styles.listInfo}>
        <div className={styles.listName}>{group.name}</div>
        <div className={styles.listMeta}>
          {memberCountLabel} · {' '}
          {recordCountLabel}
        </div>
      </div>
      <div className={`${styles.selectionIndicator} ${styles.groupIndicator}`} />
    </div>
  );
}

export default FilterModal;
